/**
 * Stripe Service for Frontend
 * Handles Stripe checkout and subscription management on the client side
 */

import { apiClient } from './apiClient';
import { isNativeApp } from '../utils/platform';

// Website URL for external purchases (iOS App Store compliance)
const WEBSITE_PRICING_URL = process.env.REACT_APP_WEBSITE_URL || 'https://promptlingo.ai';

export interface PricingPlan {
  id: string;
  name: string;
  price: number;
  interval: 'month' | 'year';
  priceId?: string;
  savings?: string;
  features: string[];
}

export interface SubscriptionStatus {
  subscription_tier: string;
  subscription_status: string;
  subscription_start_date?: string;
  subscription_end_date?: string;
  subscription_cancel_at_period_end: boolean;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
}

/**
 * Get available pricing plans
 */
export const getPricingPlans = async (): Promise<PricingPlan[]> => {
  try {
    const response = await apiClient.get('/api/stripe/pricing');
    return response.data.plans;
  } catch (error: any) {
    console.error('Failed to get pricing plans:', error);
    throw error;
  }
};

/**
 * Create a checkout session and redirect to Stripe Checkout
 * For native apps (iOS/Android), redirects to website for App Store compliance
 * @param planType - 'pro' or 'plus'
 */
export const createCheckoutSession = async (planType: 'pro' | 'plus'): Promise<void> => {
  try {
    console.log(`Creating checkout session for ${planType} plan...`);

    // On native apps, redirect to website for purchases (Apple App Store compliance)
    if (isNativeApp()) {
      console.log('Native app detected - redirecting to website for purchase');
      const pricingUrl = `${WEBSITE_PRICING_URL}/pricing?plan=${planType}`;
      // Open in system browser (outside the app)
      window.open(pricingUrl, '_system');
      return;
    }

    // On web, use Stripe checkout directly
    const response = await apiClient.post('/api/stripe/create-checkout-session', {
      planType,
    });

    // Redirect to Stripe Checkout
    if (response.data.url) {
      window.location.href = response.data.url;
    } else {
      throw new Error('No checkout URL received');
    }
  } catch (error: any) {
    console.error('Failed to create checkout session:', error);
    throw new Error(error.response?.data?.message || 'Failed to start checkout process');
  }
};

/**
 * Get subscription status for current user
 */
export const getSubscriptionStatus = async (): Promise<SubscriptionStatus> => {
  try {
    const response = await apiClient.get('/api/stripe/subscription-status');
    return response.data.subscription;
  } catch (error: any) {
    console.error('Failed to get subscription status:', error);
    throw error;
  }
};

/**
 * Create a billing portal session for subscription management
 */
export const createBillingPortalSession = async (): Promise<void> => {
  try {
    console.log('Creating billing portal session...');

    const response = await apiClient.post('/api/stripe/create-portal-session');

    // Redirect to Stripe Billing Portal
    if (response.data.url) {
      window.location.href = response.data.url;
    } else {
      throw new Error('No portal URL received');
    }
  } catch (error: any) {
    console.error('Failed to create billing portal session:', error);
    throw new Error(error.response?.data?.message || 'Failed to open billing portal');
  }
};

/**
 * Cancel subscription (at end of billing period)
 */
export const cancelSubscription = async (): Promise<void> => {
  try {
    console.log('Canceling subscription...');

    await apiClient.post('/api/stripe/cancel-subscription');

    console.log('Subscription canceled successfully');
  } catch (error: any) {
    console.error('Failed to cancel subscription:', error);
    throw new Error(error.response?.data?.message || 'Failed to cancel subscription');
  }
};

/**
 * Upgrade existing subscription to a new plan with proration
 * For native apps, redirects to website for App Store compliance
 * @param planType - 'pro' or 'plus'
 */
export const upgradeSubscription = async (planType: 'pro' | 'plus'): Promise<void> => {
  try {
    console.log(`Upgrading subscription to ${planType} plan with proration...`);

    // On native apps, redirect to website for subscription management
    if (isNativeApp()) {
      console.log('Native app detected - redirecting to website for upgrade');
      const manageUrl = `${WEBSITE_PRICING_URL}/account/subscription?upgrade=${planType}`;
      window.open(manageUrl, '_system');
      return;
    }

    const response = await apiClient.post('/api/stripe/upgrade-subscription', {
      planType,
    });

    console.log('Subscription upgraded successfully:', response.data);
  } catch (error: any) {
    console.error('Failed to upgrade subscription:', error);
    throw new Error(error.response?.data?.message || 'Failed to upgrade subscription');
  }
};